import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  User, Briefcase, Settings2, FileBadge, Mic, Video, BrainCircuit,
  CheckCircle2, Upload, Trash2, Clock, ShieldCheck, ChevronLeft, ChevronRight,
  ChevronDown, Send, Share2,
} from "lucide-react";
import { SocialShareModal } from "../components/SocialShare";
import { buildTeacherCard } from "../lib/shareCard";
import { COUNTRIES, LANGUAGES, getRegionsForCountry } from "../lib/locations";

// C15 — Simulation (was step 8) removed entirely, not deferred; "Submit
// application for review" moved to the end of Assessment (now the final
// step). The matching backend requirement that blocked submission without
// simulation uploads was removed too — see teacher_network.py.
const STEPS = [
  { n: 1, label: "Personal", icon: User },
  { n: 2, label: "Professional", icon: Briefcase },
  { n: 3, label: "Preferences", icon: Settings2 },
  { n: 4, label: "Qualifications", icon: FileBadge },
  { n: 5, label: "Recitation", icon: Mic },
  { n: 6, label: "Teaching Demo", icon: Video },
  { n: 7, label: "Assessment", icon: BrainCircuit },
];

const STATUS_LABEL = {
  admin_review: "Under admin review",
  ai_assessment_complete: "AI assessment complete — queued for review",
  documents_submitted: "Documents submitted",
  interview: "Interview scheduled",
  changes_requested: "Changes requested — please update and resubmit",
  approved: "Approved — you are part of the Teachers Network!",
  verified: "Verified Teacher ✓",
  featured: "Featured Teacher ⭐",
  rejected: "Not approved this time",
  suspended: "Suspended — contact admin",
};

/* N1 — `required` is purely a visual/accessible indicator matching what the
   backend actually enforces at submit time (/teacher-app/submit's `missing`
   checklist in teacher_network.py) — full_name, country, bio, and
   categories are the only simple form fields that gate submission; nothing
   else on this form is backend-required, so nothing else is starred. It
   doesn't add new client-side blocking validation of its own. */
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-forest-700">
      {label}
      {required && <span className="text-terracotta-500 ml-0.5" aria-hidden="true">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);
const inputCls = "w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm";

function ChipSelect({ options, values, onChange, testid }) {
  const toggle = (o) => onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`px-2.5 py-1 rounded-full text-xs border transition ${
            values.includes(o) ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300 hover:bg-sand-200"
          }`}>{o}</button>
      ))}
    </div>
  );
}

/* C7 — Languages spoken/taught used to be a ChipSelect (a sprawling grid of
   pill buttons), the only multi-value field on this form NOT styled like
   its single-value siblings (Gender, Country — a compact bordered box with
   a chevron). This closes that gap: a real dropdown trigger matching those
   fields' exact look, opening a checklist panel on tap instead of exposing
   every option inline at once. ChipSelect itself is untouched and still
   used for expertise/can_teach/categories — those are short tag lists,
   not the thing this specific complaint was about. */
function MultiSelectDropdown({ options, values, onChange, placeholder = "— Select —", testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (o) => onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);
  const summary = values.length === 0 ? placeholder
    : values.length <= 2 ? values.join(", ")
    : `${values.slice(0, 2).join(", ")} +${values.length - 2}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`${inputCls} flex items-center justify-between gap-2 text-left`}
        data-testid={testid}
      >
        <span className={`truncate ${values.length ? "" : "text-muted-foreground"}`}>{summary}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-sand-300 bg-white shadow-lg p-1.5 space-y-0.5" data-testid={`${testid}-menu`}>
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-sand-100 cursor-pointer text-sm">
              <input type="checkbox" checked={values.includes(o)} onChange={() => toggle(o)} className="rounded border-sand-300" />
              {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadRow({ onUpload, accept, label, testid, busy }) {
  return (
    <label className={`btn-ghost text-xs cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid={testid}>
      <Upload className="h-3.5 w-3.5" /> {busy ? "Uploading…" : label}
      <input type="file" accept={accept} className="hidden"
        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
    </label>
  );
}

export default function TeacherApply() {
  const [app, setApp] = useState(null);
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [surahs, setSurahs] = useState([]);
  const [recMeta, setRecMeta] = useState({ surah: 1, ayah_start: 1, ayah_end: 4 });
  const [quiz, setQuiz] = useState(null); // {attempt_id, questions, expires_at, answers}
  const [quizLeft, setQuizLeft] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = () => {
    api.get("/teacher-app").then((r) => {
      setApp(r.data);
      setForm({ ...r.data.personal, ...r.data.professional, ...r.data.preferences });
    }).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/teacher-app/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/quran/surahs").then((r) => setSurahs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!quiz?.expires_at) return;
    const t = setInterval(() => {
      const left = Math.max(0, Math.floor((new Date(quiz.expires_at) - Date.now()) / 1000));
      setQuizLeft(left);
      if (left === 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [quiz?.expires_at]);

  if (!app || !meta) return <div className="max-w-3xl mx-auto hifz-card h-64 animate-pulse-soft bg-sand-200/50" />;

  const locked = !["draft", "changes_requested"].includes(app.status);
  const f = (k) => form[k] ?? "";
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const stateOptions = getRegionsForCountry(f("country")); // C7 — State/Province options, re-derived whenever the selected country changes

  const saveStep = async (n, data) => {
    try {
      await api.put(`/teacher-app/step/${n}`, { data });
      toast.success("Saved");
      load();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); return false; }
  };

  const next = async () => {
    if (step === 1) await saveStep(1, {
      full_name: f("full_name"), preferred_name: f("preferred_name"), gender: f("gender"),
      dob: f("dob"), country: f("country"), state: f("state"), city: f("city"),
      timezone: f("timezone"), languages: form.languages || [] });
    if (step === 2) await saveStep(2, {
      years_teaching: f("years_teaching"), institution: f("institution"), position: f("position"),
      experience_summary: f("experience_summary"), bio: f("bio"), philosophy: f("philosophy"),
      expertise: form.expertise || [] });
    if (step === 3) await saveStep(3, {
      can_teach: form.can_teach || [], mode: f("mode") || "Online",
      teaching_languages: form.teaching_languages || [], categories: form.categories || [],
      hourly_rate: f("hourly_rate") ? Number(f("hourly_rate")) : 1 });
    setStep((s) => Math.min(7, s + 1));
  };

  const uploadFile = async (url, file, extra = {}) => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      fd.append(url.includes("recitation") ? "audio" : "file", file);
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success("Uploaded — Alhamdulillah");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const startQuiz = async () => {
    const r = await api.get("/teacher-app/assessment/start");
    setQuiz({ ...r.data, answers: {} });
  };
  const submitQuiz = async () => {
    setBusy(true);
    try {
      const r = await api.post("/teacher-app/assessment/submit", { attempt_id: quiz.attempt_id, answers: quiz.answers });
      toast[r.data.passed ? "success" : "error"](
        r.data.passed ? `Passed with ${r.data.percent}% — MashaAllah!` : `${r.data.percent}% — you need ${meta.assessment.pass_percent}%. You may retake it.`);
      setQuiz(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); }
    finally { setBusy(false); }
  };

  const submitApplication = async () => {
    setBusy(true);
    try {
      await api.post("/teacher-app/submit", {}, { timeout: 120000 });
      toast.success("Application submitted — our AI report is ready and admins will review you soon, in shaa Allah");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); }
    finally { setBusy(false); }
  };

  const rec = app.recitation;
  const surahMeta = surahs.find((s) => s.number === Number(recMeta.surah));

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="teacher-apply-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Global Quran Teachers Network</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Become a Verified Teacher</h1>
        <p className="mt-2 text-foreground/70">Trust, qualifications and impact — this application builds your professional reputation.</p>
      </header>

      {app.status !== "draft" && (
        <div className={`rounded-xl border-l-4 p-4 text-sm ${
          ["approved", "verified", "featured"].includes(app.status) ? "border-emerald-500 bg-emerald-500/10"
          : app.status === "rejected" ? "border-red-400 bg-red-400/10"
          : "border-gold-500 bg-gold-500/10"}`} data-testid="app-status-banner">
          <div className="font-semibold text-forest-700 inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Status: {STATUS_LABEL[app.status] || app.status}
          </div>
          {app.badges?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {app.badges.map((b) => <span key={b} className="text-xs px-2 py-0.5 rounded-full bg-sand-200">{meta.badges[b]?.split(" — ")[0] || b}</span>)}
            </div>
          )}
          {app.interview?.scheduled_at && (
            <div className="mt-1 text-xs">Interview: {new Date(app.interview.scheduled_at).toLocaleString()} {app.interview.note ? `— ${app.interview.note}` : ""}</div>
          )}
          {app.admin_notes?.length > 0 && (
            <div className="mt-1 text-xs text-foreground/70">Latest note: “{app.admin_notes[app.admin_notes.length - 1].note}”</div>
          )}
          {["approved", "verified", "featured"].includes(app.status) && (
            <>
              <button onClick={() => setShareOpen(true)} className="btn-accent text-xs mt-3" data-testid="share-teacher-card-btn">
                <Share2 className="h-3.5 w-3.5" /> Share my Verified Teacher card
              </button>
              <SocialShareModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                buildCanvas={() => buildTeacherCard({
                  name: app.personal?.preferred_name || app.personal?.full_name || "Quran Teacher",
                  country: app.personal?.country,
                  years: app.professional?.years_teaching,
                  categories: app.preferences?.categories || [],
                  badges: app.badges || [],
                  url: `${window.location.origin}/`,
                })}
                message={`Alhamdulillah — I'm a verified Quran teacher on the HikmahVerses Global Quran Teachers Network. Find your mentor:`}
                url={`${window.location.origin}/`}
                filename="verified-quran-teacher.png"
              />
            </>
          )}
        </div>
      )}

      {/* N1 — legend for the * indicators added to the genuinely-required
          fields below (Full name, Country, Biography, Teacher categories). */}
      <p className="text-xs text-muted-foreground" data-testid="required-legend">
        <span className="text-terracotta-500" aria-hidden="true">*</span> Required to submit your application
      </p>

      {/* Stepper */}
      <div className="flex flex-wrap gap-1.5" data-testid="wizard-stepper">
        {STEPS.map((s) => (
          <button key={s.n} onClick={() => setStep(s.n)}
            className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 border transition ${
              step === s.n ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300 hover:bg-sand-200"}`}
            data-testid={`step-btn-${s.n}`}>
            <s.icon className="h-3 w-3" /> {s.n}. {s.label}
          </button>
        ))}
      </div>

      <section className="hifz-card space-y-4" data-testid={`step-panel-${step}`}>
        {locked && <p className="text-xs text-muted-foreground">Your application is {STATUS_LABEL[app.status]?.toLowerCase() || "under review"} — editing is locked.</p>}

        {step === 1 && (
          <div className="grid gap-4">
            <Field label="Full name" required><input className={inputCls} value={f("full_name")} onChange={(e) => set("full_name", e.target.value)} disabled={locked} data-testid="in-full-name" required /></Field>
            <Field label="Preferred name"><input className={inputCls} value={f("preferred_name")} onChange={(e) => set("preferred_name", e.target.value)} disabled={locked} /></Field>
            <Field label="Gender">
              <select className={inputCls} value={f("gender")} onChange={(e) => set("gender", e.target.value)} disabled={locked} data-testid="in-gender">
                <option value="">—</option><option>Male</option><option>Female</option>
              </select>
            </Field>
            <Field label="Date of birth"><input type="date" className={inputCls} value={f("dob")} onChange={(e) => set("dob", e.target.value)} disabled={locked} /></Field>
            {/* C14/C7 — Country is a real dropdown (shared reference list,
                see lib/locations.js). State/Province is now one too,
                populated per-country from that same file's
                getRegionsForCountry (MIT-licensed country-region-data
                package, joined by ISO code — see locations.js for why not
                by name). Picking a new country clears any previously-set
                state, since a stale value from a different country's list
                wouldn't be valid here. City stays free text: no city
                database exists anywhere in this codebase (state/province
                data is a reasonable ~330KB; a full global city dataset
                would be 7MB+ just for this one field), and a curated list
                would just be wrong for anyone outside it — same trim
                already made for Profile.jsx's own city field. */}
            <Field label="Country" required>
              <select
                className={inputCls}
                value={f("country")}
                onChange={(e) => { set("country", e.target.value); set("state", ""); }}
                disabled={locked}
                data-testid="in-country"
                required
              >
                <option value="">—</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="State / Province">
              <select
                className={inputCls}
                value={f("state")}
                onChange={(e) => set("state", e.target.value)}
                disabled={locked || stateOptions.length === 0}
                data-testid="in-state"
              >
                <option value="">{!f("country") ? "Select a country first" : stateOptions.length ? "—" : "No regions listed"}</option>
                {stateOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="City"><input className={inputCls} value={f("city")} onChange={(e) => set("city", e.target.value)} disabled={locked} /></Field>
            <Field label="Time zone"><input className={inputCls} placeholder="e.g. GMT+3 / Asia/Riyadh" value={f("timezone")} onChange={(e) => set("timezone", e.target.value)} disabled={locked} /></Field>
            <div>
              <Field label="Languages spoken">
                <MultiSelectDropdown options={LANGUAGES} values={form.languages || []} onChange={(v) => set("languages", v)} testid="select-languages" />
              </Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {/* N1 — this field didn't exist at all before: the backend's
                submit gate has always required professional.bio, but
                nothing on this form ever collected it, so every
                application permanently failed the step-2 completeness
                check regardless of what an applicant filled in. Also used
                on its own (distinct from "Teaching philosophy" below) by
                the AI competency report generator — see
                ai_service.generate_teacher_report. */}
            <Field label="Biography" required>
              <textarea rows={3} className={inputCls} value={f("bio")} onChange={(e) => set("bio", e.target.value)} disabled={locked} required data-testid="in-bio" placeholder="A short introduction — who you are, your background, and your journey with the Qur'an." />
            </Field>
            <div className="grid gap-4">
              <Field label="Years of teaching"><input type="number" min="0" className={inputCls} value={f("years_teaching")} onChange={(e) => set("years_teaching", e.target.value)} disabled={locked} data-testid="in-years" /></Field>
              <Field label="Institution"><input className={inputCls} value={f("institution")} onChange={(e) => set("institution", e.target.value)} disabled={locked} /></Field>
              <Field label="Current position"><input className={inputCls} value={f("position")} onChange={(e) => set("position", e.target.value)} disabled={locked} /></Field>
            </div>
            <Field label="Teaching experience summary"><textarea rows={2} className={inputCls} value={f("experience_summary")} onChange={(e) => set("experience_summary", e.target.value)} disabled={locked} /></Field>
            <Field label="Teaching philosophy"><textarea rows={3} className={inputCls} value={f("philosophy")} onChange={(e) => set("philosophy", e.target.value)} disabled={locked} /></Field>
            <Field label="Areas of expertise">
              <ChipSelect options={meta.categories} values={form.expertise || []} onChange={(v) => set("expertise", v)} testid="chips-expertise" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label="I can teach"><ChipSelect options={meta.can_teach} values={form.can_teach || []} onChange={(v) => set("can_teach", v)} testid="chips-can-teach" /></Field>
            <Field label="Teaching mode">
              <select className={inputCls} value={f("mode") || "Online"} onChange={(e) => set("mode", e.target.value)} disabled={locked} data-testid="in-mode">
                <option>Online</option><option>Offline</option><option>Hybrid</option>
              </select>
            </Field>
            <Field label="Teaching languages">
              <MultiSelectDropdown options={LANGUAGES} values={form.teaching_languages || []} onChange={(v) => set("teaching_languages", v)} testid="select-teaching-languages" />
            </Field>
            <Field label="Teacher categories" required><ChipSelect options={meta.categories} values={form.categories || []} onChange={(v) => set("categories", v)} testid="chips-categories" /></Field>
            <Field label="Hourly rate">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="number" min="0" step="0.5"
                  className={`${inputCls} pl-7`}
                  value={f("hourly_rate") || 1}
                  onChange={(e) => set("hourly_rate", e.target.value)}
                  disabled={locked}
                  data-testid="in-hourly-rate"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">What you charge per hour of teaching — students see this on your profile. You can change it anytime.</p>
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <p className="text-sm text-foreground/70">Upload Ijazah, Shahadah, degrees, diplomas, certificates or recommendation letters (PDF or images, max {meta.max_upload_mb}MB each).</p>
            <div className="flex flex-wrap gap-2">
              {meta.qual_kinds.map((k) => !locked && (
                <UploadRow key={k} accept=".pdf,image/*" label={`Add ${k}`} busy={busy} testid={`upload-${k}`}
                  onUpload={(file) => uploadFile("/teacher-app/qualifications", file, { kind: k })} />
              ))}
            </div>
            {app.qualifications.length === 0 ? (
              <p className="text-xs text-muted-foreground">No documents yet.</p>
            ) : (
              <div className="space-y-2">
                {app.qualifications.map((d) => (
                  <div key={d.doc_id} className="flex items-center justify-between gap-2 rounded-xl bg-sand-200/60 px-3 py-2 text-sm" data-testid={`qual-${d.doc_id}`}>
                    <span className="truncate"><strong className="capitalize">{d.kind}</strong> — {d.filename}</span>
                    {!locked && (
                      <button onClick={async () => { await api.delete(`/teacher-app/qualifications/${d.doc_id}`); load(); }}
                        className="text-terracotta-500 hover:opacity-70" data-testid={`qual-del-${d.doc_id}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <p className="text-sm text-foreground/70">Upload a 2–5 minute recitation (MP3/WAV/M4A). Our AI evaluates accuracy, tajweed, fluency, makharij, rhythm and confidence.</p>
            {!locked && (
              <div className="grid gap-3">
                <Field label="Surah">
                  <select className={inputCls} value={recMeta.surah} onChange={(e) => setRecMeta({ ...recMeta, surah: e.target.value })} data-testid="rec-surah">
                    {surahs.map((s) => <option key={s.number} value={s.number}>{s.number}. {s.name_en}</option>)}
                  </select>
                </Field>
                <Field label="From ayah"><input type="number" min="1" max={surahMeta?.ayah_count || 286} className={inputCls} value={recMeta.ayah_start} onChange={(e) => setRecMeta({ ...recMeta, ayah_start: e.target.value })} data-testid="rec-start" /></Field>
                <Field label="To ayah"><input type="number" min="1" max={surahMeta?.ayah_count || 286} className={inputCls} value={recMeta.ayah_end} onChange={(e) => setRecMeta({ ...recMeta, ayah_end: e.target.value })} data-testid="rec-end" /></Field>
              </div>
            )}
            {!locked && (
              <UploadRow accept=".mp3,.wav,.m4a,audio/*" label="Upload recitation" busy={busy} testid="upload-recitation"
                onUpload={(file) => uploadFile("/teacher-app/recitation", file, { surah: recMeta.surah, ayah_start: recMeta.ayah_start, ayah_end: recMeta.ayah_end })} />
            )}
            {rec && (
              <div className="rounded-xl bg-sand-200/60 p-4 space-y-3" data-testid="recitation-result">
                <div className="text-sm font-semibold text-forest-700">{rec.surah_name} · Ayahs {rec.ayah_start}–{rec.ayah_end}</div>
                <audio controls src={`${process.env.REACT_APP_BACKEND_URL}/api/teacher-app/media/${rec.key}`} className="w-full" />
                <div className="grid grid-cols-3 gap-2 text-center">
                  {Object.entries(rec.ai_score).map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-white px-1 py-2">
                      <div className={`font-display font-semibold ${k === "overall" ? "text-gold-600" : "text-forest-700"}`}>{v}</div>
                      <div className="text-[9px] uppercase text-muted-foreground">{k}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <p className="text-sm text-foreground/70">Upload a 5–10 minute teaching demonstration video (max {meta.max_upload_mb}MB). Students and parents will preview your teaching style.</p>
            {!locked && <UploadRow accept="video/*" label="Upload teaching demo" busy={busy} testid="upload-demo"
              onUpload={(file) => uploadFile("/teacher-app/demo", file)} />}
            {app.demo && (
              <video controls className="w-full rounded-xl" data-testid="demo-video"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/teacher-app/media/${app.demo.key}`} />
            )}
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4">
            <p className="text-sm text-foreground/70">
              {meta.assessment.questions} randomized questions · {meta.assessment.minutes} minutes · pass ≥{meta.assessment.pass_percent}%. Topics: tajweed, hifz methodology, revision, child psychology, adult learning, fluency, ethics, fiqh, manners.
            </p>
            {app.assessment && (
              <div className={`rounded-xl p-3 text-sm ${app.assessment.passed ? "bg-emerald-500/10" : "bg-terracotta-500/10"}`} data-testid="assessment-result">
                Best result: <strong>{app.assessment.percent}%</strong> ({app.assessment.score}/{app.assessment.total}) — {app.assessment.passed ? "PASSED ✓" : "not yet passed"}
              </div>
            )}
            {!quiz && !locked && (
              <button onClick={startQuiz} className="btn-primary text-sm" data-testid="start-assessment">
                <BrainCircuit className="h-4 w-4" /> {app.assessment ? "Retake assessment" : "Start assessment"}
              </button>
            )}
            {quiz && (
              <div className="space-y-4" data-testid="quiz-panel">
                <div className="flex items-center gap-2 text-sm font-semibold text-forest-700">
                  <Clock className="h-4 w-4" /> {quizLeft != null ? `${Math.floor(quizLeft / 60)}:${String(quizLeft % 60).padStart(2, "0")}` : "…"} remaining
                </div>
                {quiz.questions.map((q, i) => (
                  <div key={q.id} className="rounded-xl bg-sand-200/50 p-3" data-testid={`quiz-q-${i}`}>
                    <div className="text-[10px] uppercase text-gold-600 font-semibold">{q.topic}</div>
                    <div className="text-sm font-medium mt-0.5">{i + 1}. {q.q}</div>
                    <div className="mt-2 space-y-1">
                      {q.options.map((o, oi) => (
                        <label key={oi} className="flex items-start gap-2 text-sm cursor-pointer">
                          <input type="radio" name={q.id} checked={quiz.answers[q.id] === oi}
                            onChange={() => setQuiz((s) => ({ ...s, answers: { ...s.answers, [q.id]: oi } }))} />
                          <span>{o}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={submitQuiz} disabled={busy || Object.keys(quiz.answers).length < quiz.questions.length}
                  className="btn-primary w-full justify-center disabled:opacity-50" data-testid="submit-assessment">
                  Submit answers ({Object.keys(quiz.answers).length}/{quiz.questions.length})
                </button>
              </div>
            )}
            {/* C15 — Simulation (formerly step 8) removed; Assessment is now
                the final step, so submission lives here instead. */}
            {!locked && (
              <button onClick={submitApplication} disabled={busy} className="btn-accent w-full justify-center disabled:opacity-50 mt-2" data-testid="submit-application">
                <Send className="h-4 w-4" /> {busy ? "Generating your AI report…" : "Submit application for review"}
              </button>
            )}
          </div>
        )}

        <div className="flex justify-between pt-2 border-t border-sand-200">
          <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="btn-ghost text-xs disabled:opacity-40" data-testid="wizard-prev">
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </button>
          {step < 7 && (
            <button onClick={next} className="btn-primary text-xs" data-testid="wizard-next">
              {step <= 3 && !locked ? "Save & continue" : "Continue"} <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </section>

      {app.ai_report && (
        <section className="hifz-card" data-testid="ai-report">
          <h2 className="font-display font-semibold text-xl text-forest-700">AI Teacher Report</h2>
          <p className="mt-2 text-sm text-foreground/80">{app.ai_report.summary}</p>
          <div className="mt-3 grid gap-3 text-sm">
            <div><strong className="text-forest-700">Recitation:</strong> {app.ai_report.recitation_quality}</div>
            <div><strong className="text-forest-700">Knowledge:</strong> {app.ai_report.knowledge_score}</div>
          </div>
          {app.ai_report.strengths?.length > 0 && (
            <div className="mt-3 text-sm"><strong className="text-emerald-600">Strengths:</strong> {app.ai_report.strengths.join(" · ")}</div>
          )}
          {app.ai_report.suggested_training?.length > 0 && (
            <div className="mt-1 text-sm"><strong className="text-gold-600">Suggested training:</strong> {app.ai_report.suggested_training.join(" · ")}</div>
          )}
        </section>
      )}
    </div>
  );
}
